import React from 'react';
import styled, { css } from 'styled-components';
import { theme, toRem } from 'design';

interface IOptionProps {
  disabled?: boolean;
  height?: number;
  shadow?: boolean;
  $selected?: boolean;
}

const ITEM_HEIGHT = 44;

const StyledOption = styled.div<IOptionProps>`
  display: flex;
  flex-shrink: 0;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: ${({ height }) => (height ? toRem(height) : toRem(ITEM_HEIGHT))};
  padding: ${toRem(theme.space.xs)};
  background-color: ${theme.color.white};
  color: ${theme.color.neutral[900]};
  cursor: pointer;
  box-shadow: ${({ shadow }) => shadow && `inset 0px -1px 0px ${theme.color.neutral[300]}`};

  div {
    flex-flow: row nowrap;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    height: 100%;
    padding: 0 ${toRem(theme.space.md)};
    border-radius: ${toRem(5.5)};
    background-color: transparent;
    word-break: break-word;
  }

  &:hover {
    div {
      background-color: ${theme.color.neutral[50]};
    }
  }

  ${({ $selected }) =>
    $selected &&
    css`
      div {
        background-color: ${theme.color.neutral[50]};
        font-weight: ${theme.font.weight.medium};
      }
    `}

  &:focus {
    div {
      border-color: rgba(0, 0, 0, 0.055);
      outline: none;
    }
  }

  ${({ disabled }) =>
    disabled &&
    css`
      color: ${theme.color.neutral[400]};
      cursor: default;

      &:hover {
        div {
          background-color: ${theme.color.white};
          color: ${theme.color.neutral[400]};
        }
      }
    `}
`;

// The selected option centres its list when it mounts, so a dropdown opens on it. Scrolling the list, not the row into view, leaves the page still.
const centre = (option: HTMLDivElement | null) => {
  const list = option?.parentElement;
  if (list) list.scrollTo({ top: option.offsetTop - (list.clientHeight - option.offsetHeight) / 2 });
};

const Option = (props: React.ComponentProps<typeof StyledOption>) => <StyledOption ref={props.$selected ? centre : undefined} {...props} />;

export default Option;
